/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/admin/MobileOperatorsModal.tsx
 * Release: v1.0.0 - Modale gestione operatori mobile
 * Data: 2026-01-18
 * Descrizione: Modale CRUD completo per operatori mobile
 */

import React, { useState, useEffect, useCallback } from 'react';
import { appToast } from '../../lib/appDialog';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  UserPlus,
  Key,
  Lock,
  Unlock,
  Trash2,
  RefreshCw,
  AlertTriangle,
  X,
  Smartphone,
  Pencil
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';

interface MobileOperator {
  id: number;
  username: string;
  displayName: string;
  role: 'officina' | 'accettazione' | 'magazzino' | 'admin';
  active: boolean;
  pinMustChange: boolean;
  failedAttempts: number;
  lockedUntil: string | null;
  lastLogin: string | null;
  createdAt: string;
  sede_assegnata?: 'misterbianco' | 'palermo' | null;
}

interface NewOperatorForm {
  displayName: string;
  role: 'officina' | 'accettazione' | 'magazzino' | 'admin';
  pin?: string;
  sede_assegnata?: 'misterbianco' | 'palermo' | null;
  solo_delega?: boolean;
}

interface MobileOperatorsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileOperatorsModal: React.FC<MobileOperatorsModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [operators, setOperators] = useState<MobileOperator[]>([]);
  const [loading, setLoading] = useState(true);
  // Default: mostra solo operatori ATTIVI. La delete è un soft-delete (active=false):
  // senza questo filtro i "cancellati" restano visibili e sembra che la delete non funzioni
  // (e i re-inserimenti si accumulano). Il dropdown permette di vedere "Tutti"/"Disabilitati".
  const [filter, setFilter] = useState({ role: '', active: 'true' });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newOperator, setNewOperator] = useState<NewOperatorForm>({
    displayName: '',
    role: 'officina',
    sede_assegnata: 'misterbianco',
    solo_delega: false
  });
  const [generatedPin, setGeneratedPin] = useState<string | null>(null);
  const [resetPinResult, setResetPinResult] = useState<{ operatorId: number; pin: string } | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'delete' | 'resetPin' | 'toggleActive';
    operatorId: number;
    operatorName: string;
    currentActive?: boolean;
    step?: number;
  } | null>(null);
  const [editingOperator, setEditingOperator] = useState<MobileOperator | null>(null);
  const [editForm, setEditForm] = useState<{ displayName: string; role: string; newPin: string; sede_assegnata: string }>({ displayName: '', role: '', newPin: '', sede_assegnata: '' });
  const [editSuccess, setEditSuccess] = useState<{ operatorName: string; pin?: string } | null>(null);

  // Verifica permessi
  const canDelete = user?.role === 'admin' || user?.role === 'superadmin';

  // Carica operatori
  const loadOperators = useCallback(async () => {
    if (!isOpen) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.role) params.append('role', filter.role);
      if (filter.active) params.append('active', filter.active);

      const response = await fetch(
        `/api/mobile-operators/admin/operators?${params}`
      );
      const data = await response.json();

      if (data.success) {
        setOperators(data.operators);
      }
    } catch (error) {
      console.error('Errore caricamento operatori:', error);
    } finally {
      setLoading(false);
    }
  }, [isOpen, filter]);

  useEffect(() => {
    if (isOpen) {
      loadOperators();
      setShowCreateForm(false);
      setGeneratedPin(null);
      setResetPinResult(null);
      setConfirmAction(null);
      setEditingOperator(null);
      setEditSuccess(null);
    }
  }, [isOpen, loadOperators]);

  // Crea operatore
  const handleCreateOperator = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await fetch('/api/mobile-operators/admin/operators', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newOperator)
      });

      const data = await response.json();

      if (data.success) {
        if (data.operator.pin) {
          setGeneratedPin(data.operator.pin);
        }
        loadOperators();
        setNewOperator({ displayName: '', role: 'officina', sede_assegnata: 'misterbianco', solo_delega: false });
      } else {
        appToast.error('Errore: ' + data.error);
      }
    } catch (error) {
      console.error('Errore creazione operatore:', error);
      appToast.error('Errore durante la creazione');
    }
  };

  // Reset PIN
  const handleResetPIN = async (operatorId: number) => {
    try {
      const response = await fetch(
        `/api/mobile-operators/admin/operators/${operatorId}/reset-pin`,
        { method: 'POST' }
      );

      const data = await response.json();

      if (data.success) {
        setResetPinResult({ operatorId, pin: data.newPin });
        setConfirmAction(null);
        loadOperators();
      } else {
        appToast.error('Errore: ' + data.error);
      }
    } catch (error) {
      console.error('Errore reset PIN:', error);
    }
  };

  // Toggle active
  const handleToggleActive = async (operatorId: number, currentActive: boolean) => {
    try {
      const response = await fetch(
        `/api/mobile-operators/admin/operators/${operatorId}/toggle-active`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ active: !currentActive })
        }
      );

      const data = await response.json();

      if (data.success) {
        setConfirmAction(null);
        loadOperators();
      } else {
        appToast.error('Errore: ' + data.error);
      }
    } catch (error) {
      console.error('Errore toggle active:', error);
    }
  };

  // Elimina operatore
  const handleDeleteOperator = async (operatorId: number) => {
    try {
      const response = await fetch(
        `/api/mobile-operators/admin/operators/${operatorId}`,
        { method: 'DELETE' }
      );

      const data = await response.json();

      if (data.success) {
        setConfirmAction(null);
        loadOperators();
      } else {
        appToast.error('Errore: ' + data.error);
      }
    } catch (error) {
      console.error('Errore eliminazione:', error);
    }
  };

  // Inizia modifica operatore
  const handleStartEdit = (operator: MobileOperator) => {
    setEditingOperator(operator);
    setEditForm({
      displayName: operator.displayName,
      role: operator.role,
      newPin: '',
      sede_assegnata: operator.sede_assegnata || ''
    });
  };

  // Salva modifiche operatore
  const handleSaveEdit = async () => {
    if (!editingOperator) return;

    // Valida PIN se inserito
    if (editForm.newPin && !/^\d{4}$/.test(editForm.newPin)) {
      appToast.error('Il PIN deve essere composto da 4 cifre');
      return;
    }

    try {
      const updateData: Record<string, string | null> = {
        displayName: editForm.displayName,
        role: editForm.role,
        sede_assegnata: editForm.sede_assegnata || null
      };

      // Aggiungi PIN solo se specificato
      if (editForm.newPin) {
        updateData.pin = editForm.newPin;
      }

      const response = await fetch(
        `/api/mobile-operators/admin/operators/${editingOperator.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateData)
        }
      );

      const data = await response.json();

      if (data.success) {
        const operatorName = editingOperator.displayName;
        const updatedPin = editForm.newPin || undefined;
        setEditingOperator(null);
        loadOperators();
        setEditSuccess({ operatorName, pin: updatedPin });
      } else {
        appToast.error('Errore: ' + data.error);
      }
    } catch (error) {
      console.error('Errore modifica operatore:', error);
      appToast.error('Errore durante la modifica');
    }
  };

  // Sblocca account
  const handleUnlockAccount = async (operatorId: number) => {
    try {
      const response = await fetch(
        `/api/mobile-operators/admin/operators/${operatorId}/unlock`,
        { method: 'POST' }
      );

      const data = await response.json();

      if (data.success) {
        loadOperators();
      } else {
        appToast.error('Errore: ' + data.error);
      }
    } catch (error) {
      console.error('Errore sblocco:', error);
    }
  };

  // Helper badge colore ruolo
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'officina': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'accettazione': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'magazzino': return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      case 'admin': return 'bg-red-500/20 text-red-300 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      officina: 'Officina', accettazione: 'Accettazione',
      magazzino: 'Magazzino', admin: 'Admin'
    };
    return labels[role] || role;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-zinc-800 rounded-2xl shadow-2xl border border-zinc-700 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700 bg-zinc-900/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-500/20 rounded-lg">
                  <Smartphone className="w-5 h-5 text-cyan-500" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Gestione Operatori Mobile</h2>
                  <p className="text-xs text-gray-400">CRUD operatori webapp smartphone</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Toolbar */}
            <div className="px-6 py-3 border-b border-zinc-700/50 bg-zinc-800/50">
              <div className="flex flex-wrap gap-3 items-center justify-between">
                <div className="flex gap-2">
                  <select
                    value={filter.role}
                    onChange={(e) => setFilter({ ...filter, role: e.target.value })}
                    className="px-3 py-1.5 bg-zinc-700 border border-zinc-600 text-white text-sm rounded-lg"
                  >
                    <option value="">Tutti i ruoli</option>
                    <option value="officina">Officina</option>
                    <option value="accettazione">Accettazione</option>
                    <option value="magazzino">Magazzino</option>
                    <option value="admin">Admin</option>
                  </select>

                  <select
                    value={filter.active}
                    onChange={(e) => setFilter({ ...filter, active: e.target.value })}
                    className="px-3 py-1.5 bg-zinc-700 border border-zinc-600 text-white text-sm rounded-lg"
                  >
                    <option value="">Tutti</option>
                    <option value="true">Attivi</option>
                    <option value="false">Disabilitati</option>
                  </select>

                  <button
                    onClick={() => loadOperators()}
                    className="p-1.5 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-colors"
                    title="Ricarica"
                  >
                    <RefreshCw className={`w-4 h-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                <button
                  onClick={() => setShowCreateForm(!showCreateForm)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-sm rounded-lg transition-colors"
                >
                  <UserPlus className="w-4 h-4" />
                  Nuovo
                </button>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Form creazione */}
              {showCreateForm && (
                <div className="bg-zinc-700/30 rounded-xl p-4 mb-4 border border-zinc-600/50">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-cyan-400" />
                    Nuovo Operatore
                  </h3>
                  <form onSubmit={handleCreateOperator} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <input
                        type="text"
                        value={newOperator.displayName}
                        onChange={(e) => setNewOperator({ ...newOperator, displayName: e.target.value })}
                        placeholder="Nome completo"
                        className="px-3 py-2 bg-zinc-800 border border-zinc-600 text-white rounded-lg text-sm"
                        required
                      />
                      <select
                        value={newOperator.role}
                        onChange={(e) => setNewOperator({ ...newOperator, role: e.target.value as any })}
                        className="px-3 py-2 bg-zinc-800 border border-zinc-600 text-white rounded-lg text-sm"
                      >
                        <option value="officina">Officina</option>
                        <option value="accettazione">Accettazione</option>
                        <option value="magazzino">Magazzino</option>
                        <option value="admin">Admin</option>
                      </select>
                      <select
                        value={newOperator.sede_assegnata || 'misterbianco'}
                        onChange={(e) => setNewOperator({ ...newOperator, sede_assegnata: e.target.value as 'misterbianco' | 'palermo' })}
                        className="px-3 py-2 bg-zinc-800 border border-zinc-600 text-white rounded-lg text-sm"
                      >
                        <option value="misterbianco">🔵 Misterbianco</option>
                        <option value="palermo">🟠 Palermo</option>
                      </select>
                      <input
                        type="text"
                        value={newOperator.pin || ''}
                        onChange={(e) => setNewOperator({ ...newOperator, pin: e.target.value })}
                        placeholder="PIN (auto se vuoto)"
                        pattern="[0-9]{4}"
                        maxLength={4}
                        className="px-3 py-2 bg-zinc-800 border border-zinc-600 text-white rounded-lg text-sm"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-300 mb-2 cursor-pointer">
                      <input type="checkbox" checked={!!newOperator.solo_delega}
                        onChange={(e) => setNewOperator({ ...newOperator, solo_delega: e.target.checked })} />
                      Senza credenziali (solo delega — niente login, solo selezionabile come "eseguito da")
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm rounded-lg transition-colors"
                      >
                        Crea
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowCreateForm(false); setGeneratedPin(null); }}
                        className="px-4 py-2 bg-zinc-600 hover:bg-zinc-500 text-white text-sm rounded-lg transition-colors"
                      >
                        Annulla
                      </button>
                    </div>
                  </form>

                  {/* PIN Generato */}
                  {generatedPin && (
                    <div className="mt-3 bg-green-500/20 border border-green-500/50 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Key className="w-5 h-5 text-green-400" />
                          <div>
                            <p className="text-green-300 text-sm">PIN generato:</p>
                            <p className="text-2xl font-mono font-bold text-green-400">{generatedPin}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setGeneratedPin(null)}
                          className="p-1.5 hover:bg-green-500/30 rounded-lg transition-colors"
                          title="Chiudi"
                        >
                          <X className="w-4 h-4 text-green-400" />
                        </button>
                      </div>
                      <p className="text-xs text-green-300 mt-2">
                        Comunica questo PIN all'operatore. Non sarà più recuperabile.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* PIN Reset Result */}
              {resetPinResult && (
                <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Key className="w-5 h-5 text-green-400" />
                      <div>
                        <p className="text-green-300 text-sm">Nuovo PIN:</p>
                        <p className="text-3xl font-mono font-bold text-green-400">{resetPinResult.pin}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setResetPinResult(null)}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-sm rounded-lg"
                    >
                      OK
                    </button>
                  </div>
                </div>
              )}

              {/* Edit Success */}
              {editSuccess && (
                <div className="bg-cyan-500/20 border border-cyan-500/50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Pencil className="w-5 h-5 text-cyan-400" />
                      <div>
                        <p className="text-cyan-300 text-sm">
                          Operatore <strong>{editSuccess.operatorName}</strong> aggiornato con successo!
                        </p>
                        {editSuccess.pin && (
                          <div className="mt-2">
                            <p className="text-cyan-300 text-xs">Nuovo PIN:</p>
                            <p className="text-2xl font-mono font-bold text-cyan-400">{editSuccess.pin}</p>
                            <p className="text-xs text-cyan-300 mt-1">
                              Comunica questo PIN all'operatore.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setEditSuccess(null)}
                      className="p-1.5 hover:bg-cyan-500/30 rounded-lg transition-colors"
                      title="Chiudi"
                    >
                      <X className="w-4 h-4 text-cyan-400" />
                    </button>
                  </div>
                </div>
              )}

              {/* Edit Form */}
              {editingOperator && (
                <div className="bg-cyan-500/20 border border-cyan-500/50 rounded-xl p-4 mb-4">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Pencil className="w-4 h-4 text-cyan-400" />
                    Modifica Operatore: <span className="font-mono text-cyan-300">{editingOperator.username}</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Nome completo</label>
                      <input
                        type="text"
                        value={editForm.displayName}
                        onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white rounded-lg text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Ruolo</label>
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white rounded-lg text-sm"
                      >
                        <option value="officina">Officina</option>
                        <option value="accettazione">Accettazione</option>
                        <option value="magazzino">Magazzino</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Sede</label>
                      <select
                        value={editForm.sede_assegnata}
                        onChange={(e) => setEditForm({ ...editForm, sede_assegnata: e.target.value })}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white rounded-lg text-sm"
                      >
                        <option value="">Non assegnata</option>
                        <option value="misterbianco">Misterbianco</option>
                        <option value="palermo">Palermo</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Nuovo PIN (opzionale)</label>
                      <input
                        type="text"
                        value={editForm.newPin}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setEditForm({ ...editForm, newPin: val });
                        }}
                        placeholder="Lascia vuoto per non cambiare"
                        pattern="[0-9]{4}"
                        maxLength={4}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 text-white rounded-lg text-sm font-mono"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveEdit}
                      className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm rounded-lg transition-colors"
                    >
                      Salva
                    </button>
                    <button
                      onClick={() => setEditingOperator(null)}
                      className="px-4 py-2 bg-zinc-600 hover:bg-zinc-500 text-white text-sm rounded-lg transition-colors"
                    >
                      Annulla
                    </button>
                  </div>
                </div>
              )}

              {/* Confirm Action */}
              {confirmAction && (
                <div className={`border rounded-xl p-4 mb-4 ${
                  confirmAction.type === 'delete' ? 'bg-red-500/20 border-red-500/50' :
                  confirmAction.type === 'toggleActive' ? 'bg-yellow-500/20 border-yellow-500/50' :
                  'bg-blue-500/20 border-blue-500/50'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <AlertTriangle className={`w-5 h-5 ${
                      confirmAction.type === 'delete' ? 'text-red-400' :
                      confirmAction.type === 'toggleActive' ? 'text-yellow-400' : 'text-blue-400'
                    }`} />
                    <p className="text-white font-medium">
                      {confirmAction.type === 'delete' ? 'Conferma eliminazione' :
                       confirmAction.type === 'toggleActive' ? (confirmAction.currentActive ? 'Conferma disabilitazione' : 'Conferma riabilitazione') :
                       'Conferma reset PIN'} di <strong>{confirmAction.operatorName}</strong>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmAction(null)}
                      className="px-3 py-1.5 bg-zinc-600 hover:bg-zinc-500 text-white text-sm rounded-lg"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={() => {
                        if (confirmAction.type === 'delete') {
                          if (confirmAction.step === 1) {
                            setConfirmAction({ ...confirmAction, step: 2 });
                          } else {
                            handleDeleteOperator(confirmAction.operatorId);
                          }
                        } else if (confirmAction.type === 'toggleActive') {
                          handleToggleActive(confirmAction.operatorId, confirmAction.currentActive!);
                        } else {
                          handleResetPIN(confirmAction.operatorId);
                        }
                      }}
                      className={`px-3 py-1.5 text-white text-sm rounded-lg ${
                        confirmAction.type === 'delete'
                          ? (confirmAction.step === 2 ? 'bg-red-700 hover:bg-red-600 animate-pulse' : 'bg-red-600 hover:bg-red-500')
                          : confirmAction.type === 'toggleActive' ? 'bg-yellow-600 hover:bg-yellow-500'
                          : 'bg-blue-600 hover:bg-blue-500'
                      }`}
                    >
                      {confirmAction.type === 'delete' && confirmAction.step === 2 ? 'ELIMINA DEFINITIVAMENTE' : 'Conferma'}
                    </button>
                  </div>
                </div>
              )}

              {/* Tabella operatori */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-cyan-500 animate-spin" />
                  <span className="ml-2 text-gray-400">Caricamento...</span>
                </div>
              ) : operators.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">Nessun operatore trovato</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-700/50 text-gray-300">
                      <tr>
                        <th className="text-left p-3 font-medium">Username</th>
                        <th className="text-left p-3 font-medium">Nome</th>
                        <th className="text-left p-3 font-medium">Ruolo</th>
                        <th className="text-left p-3 font-medium">Sede</th>
                        <th className="text-left p-3 font-medium">Stato</th>
                        <th className="text-left p-3 font-medium">Ultimo accesso</th>
                        <th className="text-right p-3 font-medium">Azioni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-700/50">
                      {operators.map((op) => (
                        <tr key={op.id} className="hover:bg-zinc-700/30">
                          <td className="p-3 font-mono text-gray-300">{op.username}</td>
                          <td className="p-3 text-white">{op.displayName}</td>
                          <td className="p-3">
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs border ${getRoleBadgeColor(op.role)}`}>
                              {getRoleLabel(op.role)}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs border ${
                              op.sede_assegnata === 'misterbianco'
                                ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                                : op.sede_assegnata === 'palermo'
                                  ? 'bg-orange-500/20 text-orange-300 border-orange-500/30'
                                  : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                            }`}>
                              {op.sede_assegnata === 'misterbianco' ? '🔵 MB' : op.sede_assegnata === 'palermo' ? '🟠 PA' : '—'}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex flex-col gap-1">
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs border ${
                                op.active ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                              }`}>
                                {op.active ? 'Attivo' : 'Disabilitato'}
                              </span>
                              {op.lockedUntil && new Date(op.lockedUntil) > new Date() && (
                                <span className="inline-flex px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-300 border border-red-500/30">
                                  Bloccato
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-gray-400">
                            {op.lastLogin ? new Date(op.lastLogin).toLocaleDateString('it-IT') : 'Mai'}
                          </td>
                          <td className="p-3">
                            <div className="flex justify-end gap-1">
                              <button
                                onClick={() => handleStartEdit(op)}
                                className="p-1.5 text-cyan-400 hover:bg-cyan-500/20 rounded transition-colors"
                                title="Modifica"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setConfirmAction({ type: 'resetPin', operatorId: op.id, operatorName: op.displayName })}
                                className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
                                title="Reset PIN"
                              >
                                <Key className="w-4 h-4" />
                              </button>
                              {op.lockedUntil && new Date(op.lockedUntil) > new Date() && (
                                <button
                                  onClick={() => handleUnlockAccount(op.id)}
                                  className="p-1.5 text-green-400 hover:bg-green-500/20 rounded transition-colors"
                                  title="Sblocca"
                                >
                                  <Unlock className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => setConfirmAction({ type: 'toggleActive', operatorId: op.id, operatorName: op.displayName, currentActive: op.active })}
                                className={`p-1.5 ${op.active ? 'text-yellow-400 hover:bg-yellow-500/20' : 'text-green-400 hover:bg-green-500/20'} rounded transition-colors`}
                                title={op.active ? 'Disabilita' : 'Riabilita'}
                              >
                                {op.active ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                              </button>
                              {canDelete && (
                                <button
                                  onClick={() => setConfirmAction({ type: 'delete', operatorId: op.id, operatorName: op.displayName, step: 1 })}
                                  className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                                  title="Elimina"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-zinc-700 bg-zinc-900/50">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">
                  {operators.length} operatore{operators.length !== 1 ? 'i' : ''}
                </span>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white text-sm rounded-lg transition-colors"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileOperatorsModal;
